import api from './api';

/**
 * 希望シフトの収集。
 *
 * ## 定型 + その月の提出の2層
 *
 * 定型（曜日ごとの既定）を敷いた状態で提出画面を開き、違う日だけ直す。
 * 固定の人はほぼ何も出さずに済む。
 *
 * ## 後出しはフラグではなく比較で決まる
 *
 * `isLate` はサーバが `submitted_at` と締切を比べて返す。締切を延ばせば解消される。
 */

export type AvailabilityKind = 'available' | 'unavailable';

export interface RequestPeriod {
  id: number;
  name: string;
  dateFrom: string;
  dateTo: string;
  deadlineAt: string;
  /** 手のスイッチ。`closed` = 「シフト提出を締め切る」を押した */
  status: 'open' | 'closed';
  /** 期限内の再提出を許可するか。**初回提出には効かない** */
  allowResubmit: boolean;
  /** 締切(時刻)と同時にシフト提出を締め切るか */
  closeAtDeadline: boolean;
  /**
   * **シフト提出が締め切られているか**（サーバが導出して返す）。
   * 手で締め切ったか、`closeAtDeadline` で時刻を過ぎたか。
   * 画面はこれを見て提出を閉じ、**公開してよいか**を決める。
   */
  submissionClosed: boolean;
  /** この期間のシフトを公開した日時。null なら未公開（「一部公開」は存在しない） */
  shiftPublishedAt: string | null;
}

export interface AvailabilityEntry {
  id: number;
  date: string;
  kind: AvailabilityKind;
  startTime: string | null;
  endTime: string | null;
  note: string | null;
  /** 提出のときに「いつもの予定」から起こした行か。画面の出し分けにだけ使う */
  fromDefault?: boolean;
}

export interface AvailabilityDefault {
  weekday: number;
  kind: AvailabilityKind;
  startTime: string | null;
  endTime: string | null;
}

export interface SlotInput {
  kind: AvailabilityKind;
  startTime?: string | null;
  endTime?: string | null;
  note?: string | null;
}

export interface SubmissionMember {
  userId: number;
  name: string;
  employeeNumber: string | null;
  submittedAt: string | null;
  entryCount: number;
  isLate: boolean;
  workScheduleType: 'fixed' | 'shift';
  /** 締め切ったあとに対象者へ足された時刻。出すと NULL に戻る */
  reopenedAt: string | null;
}

export interface ShiftPreferences {
  monthlyMinHours: number | null;
  monthlyMaxHours: number | null;
  weeklyMinHours: number | null;
  weeklyMaxHours: number | null;
}

/**
 * 受付の一覧。
 *
 * ⚠️ **出す側の画面は `mine: true` で呼ぶ。** 組む側の権限を持つ人(店長)でも、
 * 自分の提出画面には**自分が対象の受付だけ**を出す。役割で切ると、
 * 対象ですらない受付が「未提出」と出たまま残る(代表指摘 2026-09-02)。
 */
export const listPeriods = async (opts?: { mine?: boolean }): Promise<RequestPeriod[]> => {
  const r = await api.get('/shift-requests/periods', { params: opts?.mine ? { mine: 1 } : undefined });
  return r.data.periods;
};

export const createPeriod = async (p: {
  name: string;
  dateFrom: string;
  dateTo: string;
  deadlineAt: string;
  /** 省略すると**シフト制の在職者が既定の対象**になる */
  userIds?: number[];
  /** 期限内の再提出を許可するか（既定は許可） */
  allowResubmit?: boolean;
  /** 締切と同時にシフト提出を締め切るか */
  closeAtDeadline?: boolean;
}): Promise<RequestPeriod> => {
  const r = await api.post('/shift-requests/periods', p);
  return r.data.period;
};

/**
 * 受付を直す。
 *
 * ⚠️ **対象期間を狭めると、範囲外の希望が消える。** サーバは件数を数えて
 * 409 `WOULD_DROP_ENTRIES` を返すので、確認を取ってから `confirm: true` で再送する。
 *
 * `resubmit: true` は**期間を変えたときだけ効く**。提出の記録を消して全員を
 * 「未提出」に戻す（希望の中身は残る）。増えた日を本人に埋めてもらうための操作。
 */
export const updatePeriod = async (
  id: number,
  p: {
    name?: string;
    dateFrom?: string;
    dateTo?: string;
    deadlineAt?: string;
    status?: 'open' | 'closed';
    confirm?: boolean;
    /** この1回の操作: 提出済みを「未提出」に戻すか（期間を変えたときだけ効く） */
    resubmit?: boolean;
    /** 受付の設定: 期限内の再提出を許可するか */
    allowResubmit?: boolean;
    /** 受付の設定: 締切と同時にシフト提出を締め切るか */
    closeAtDeadline?: boolean;
  }
): Promise<RequestPeriod> => {
  const r = await api.put(`/shift-requests/periods/${id}`, p);
  return r.data.period;
};

/** 提出が1件でもあると 409 `PERIOD_IN_USE` で断られる */
export const deletePeriod = async (id: number): Promise<void> => {
  await api.delete(`/shift-requests/periods/${id}`);
};

/** 提出状況。**未提出者が上に来る**（誰に声を掛けるかが分かるように） */
export const getPeriodStatus = async (
  id: number
): Promise<{ period: RequestPeriod; members: SubmissionMember[] }> => {
  const r = await api.get(`/shift-requests/periods/${id}/status`);
  return r.data;
};

export const getMyRequest = async (
  periodId: number,
  userId?: number
): Promise<{
  period: RequestPeriod;
  userId: number;
  /** この受付の対象者か。外されていると読むだけになる */
  isTarget: boolean;
  submittedAt: string | null;
  isLate: boolean;
  entries: AvailabilityEntry[];
  defaults: AvailabilityDefault[];
}> => {
  const r = await api.get(`/shift-requests/${periodId}/mine`, { params: { userId } });
  return r.data;
};

/** 1日ぶんを丸ごと置き換える。`slots: []` で「定型のまま」に戻る */
export const saveDayRequest = async (
  periodId: number,
  date: string,
  slots: SlotInput[],
  userId?: number
): Promise<void> => {
  await api.put(`/shift-requests/${periodId}/mine`, { date, slots, userId });
};

export const submitRequest = async (
  periodId: number
): Promise<{ submittedAt: string; isLate: boolean }> => {
  const r = await api.post(`/shift-requests/${periodId}/submit`);
  return r.data;
};

export const getDefaults = async (userId?: number): Promise<AvailabilityDefault[]> => {
  const r = await api.get('/shift-requests/defaults', { params: { userId } });
  return r.data.defaults;
};

export const saveDefaults = async (weekday: number, slots: SlotInput[], userId?: number): Promise<void> => {
  await api.put('/shift-requests/defaults', { weekday, slots, userId });
};

export const getPreferences = async (userId?: number): Promise<ShiftPreferences | null> => {
  const r = await api.get('/shift-requests/preferences', { params: { userId } });
  return r.data.preferences;
};

export const savePreferences = async (p: ShiftPreferences, userId?: number): Promise<void> => {
  await api.put('/shift-requests/preferences', { ...p, userId });
};

/**
 * 対象者を入れ替える。固定制の人を臨時に足す・外すのに使う。
 *
 * ⚠️ **締め切ったあとに足した人には、その人だけ提出を開ける**（`reopened`）。
 * 月の途中で入った人に希望を出してもらうため。何人開いたかを返す。
 */
export const updateTargets = async (
  periodId: number,
  userIds: number[]
): Promise<{ count: number; reopened: number }> => {
  const r = await api.put(`/shift-requests/periods/${periodId}/targets`, { userIds });
  return r.data;
};
